import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Plus, Edit2, Trash2, Clock, GripVertical, ChevronUp, ChevronDown, Users } from "lucide-react";

export default function Services() {
  const [services, setServices] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ name: '', category: 'Nails', description: '', duration_minutes: 60, price: 0, rebook_cadence_days: 21, active: true, sort_order: 100, group_size: 1, pricing_type: 'per_group', length_pricing_enabled: false, extras: [] });

  const load = async () => {
    try {
      const res = await axios.get(`${API}/services`);
      setServices(res.data);
    } catch (e) {  }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ name: '', category: 'Nails', description: '', duration_minutes: 60, price: 0, rebook_cadence_days: 21, active: true, sort_order: (services.length + 1) * 10, group_size: 1, pricing_type: 'per_group', length_pricing_enabled: false, extras: [] });
    setDialog('new');
  };

  const openEdit = (svc) => {
    setForm({ ...svc });
    setDialog(svc);
  };

  const save = async () => {
    try {
      if (dialog === 'new') {
        await axios.post(`${API}/services`, form);
        toast.success('Service created');
      } else {
        await axios.put(`${API}/services/${dialog.id}`, form);
        toast.success('Service updated');
      }
      setDialog(null);
      load();
    } catch (e) { toast.error('Failed to save'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try {
      await axios.delete(`${API}/services/${id}`);
      toast.success('Service deleted');
      load();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const moveService = async (index, direction) => {
    const newServices = [...services];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newServices.length) return;
    [newServices[index], newServices[targetIndex]] = [newServices[targetIndex], newServices[index]];
    // Update sort_order
    const order = newServices.map((s, i) => ({ id: s.id, sort_order: i * 10 }));
    setServices(newServices);
    try {
      await axios.post(`${API}/services/reorder`, { order });
    } catch (e) {  }
  };

  // Group by category
  const categories = [...new Set(services.map(s => s.category || 'Other'))];

  return (
    <div className="space-y-4 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Services</h1>
          <p className="text-sm text-muted-foreground">{services.length} services in {categories.length} categories</p>
        </div>
        <Button onClick={openNew} className="rounded-xl" data-testid="add-service-button">
          <Plus size={16} className="mr-1" /> Add Service
        </Button>
      </div>

      {categories.map(cat => {
        const catServices = services.filter(s => (s.category || 'Other') === cat);
        return (
          <div key={cat}>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-primary" style={{ fontFamily: 'Playfair Display' }}>{cat}</h3>
              <Badge variant="secondary" className="text-xs rounded-full">{catServices.length}</Badge>
            </div>
            <div className="space-y-2 mb-6">
              {catServices.map((svc, idx) => {
                const globalIdx = services.indexOf(svc);
                return (
                  <Card key={svc.id} className={`p-3 rounded-xl border shadow-sm hover:shadow-md transition-shadow ${!svc.active ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-3">
                      {/* Reorder buttons */}
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button onClick={() => moveService(globalIdx, -1)} className="text-muted-foreground hover:text-primary p-0.5 rounded" disabled={globalIdx === 0}>
                          <ChevronUp size={14} />
                        </button>
                        <button onClick={() => moveService(globalIdx, 1)} className="text-muted-foreground hover:text-primary p-0.5 rounded" disabled={globalIdx === services.length - 1}>
                          <ChevronDown size={14} />
                        </button>
                      </div>
                      {/* Service info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-sm truncate" style={{ fontFamily: 'Playfair Display' }}>{svc.name}</h4>
                          {!svc.active && <Badge variant="outline" className="text-[10px]">Inactive</Badge>}
                          {(svc.group_size || 1) > 1 && (
                            <Badge className="text-[9px] bg-purple-100 text-purple-700 rounded-full flex items-center gap-0.5">
                              <Users size={9} /> {svc.group_size}p
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{svc.description}</p>
                      </div>
                      {/* Price & duration */}
                      <div className="text-right shrink-0">
                        <p className="text-sm font-semibold text-primary">£{(svc.price || 0).toFixed(2)}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end"><Clock size={10} />{svc.duration_minutes} min</p>
                      </div>
                      {/* Actions */}
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(svc)} className="h-7 w-7 rounded-lg"><Edit2 size={12} /></Button>
                        <Button variant="ghost" size="icon" onClick={() => remove(svc.id)} className="h-7 w-7 rounded-lg text-destructive"><Trash2 size={12} /></Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}

      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display' }}>{dialog === 'new' ? 'Add Service' : 'Edit Service'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Service Name</Label>
                <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} className="mt-1 rounded-xl" placeholder="e.g., BIAB Full Set" />
              </div>
              <div>
                <Label>Category / Service Type</Label>
                <Input value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))} className="mt-1 rounded-xl" placeholder="e.g., BIAB, Acrylic, Specials" />
                <p className="text-[10px] text-muted-foreground mt-1">Services are grouped by this on the booking page</p>
              </div>
              <div>
                <Label>Price (£)</Label>
                <Input type="number" value={form.price} onChange={e => setForm(p => ({...p, price: parseFloat(e.target.value) || 0}))} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" value={form.duration_minutes} onChange={e => setForm(p => ({...p, duration_minutes: parseInt(e.target.value) || 0}))} className="mt-1 rounded-xl" />
              </div>
              <div>
                <Label>Rebook cadence (days)</Label>
                <Input type="number" value={form.rebook_cadence_days} onChange={e => setForm(p => ({...p, rebook_cadence_days: parseInt(e.target.value) || 0}))} className="mt-1 rounded-xl" />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} className="mt-1 rounded-xl" rows={2} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} />
                <Label>Active</Label>
              </div>
            </div>

            <Separator />

            {/* Group booking */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs font-semibold">Group Booking Settings</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">Set to 1 for individual bookings. Set higher for group sessions (e.g. 4 for a group of 4).</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs"># People (max per slot)</Label>
                  <Input
                    type="number" min={1} max={20}
                    value={form.group_size || 1}
                    onChange={e => setForm(p => ({...p, group_size: parseInt(e.target.value) || 1}))}
                    className="mt-1 rounded-xl"
                  />
                  <p className="text-[10px] text-muted-foreground mt-1">1 = individual · 2+ = group</p>
                </div>
                {(form.group_size || 1) > 1 && (
                  <div>
                    <Label className="text-xs">Pricing Type</Label>
                    <Select value={form.pricing_type || 'per_group'} onValueChange={v => setForm(p => ({...p, pricing_type: v}))}>
                      <SelectTrigger className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="per_group">Per group (flat fee)</SelectItem>
                        <SelectItem value="per_person">Per person (price × people)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {form.pricing_type === 'per_group'
                        ? `£${form.price || 0} total for all ${form.group_size} people`
                        : `£${(form.price || 0) * (form.group_size || 1)} total (£${form.price || 0} × ${form.group_size || 1})`}
                    </p>
                  </div>
                )}
              </div>

              {/* Eligibility window — restrict service to clients within N days of last visit */}
              <Separator className="my-3" />
              <div>
                <Label className="text-xs font-medium">Eligibility Window (days since last visit)</Label>
                <p className="text-[10px] text-muted-foreground mb-2">For "infill" / "top-up" type services. Leave blank/0 for no restriction.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Min days</Label>
                    <Input
                      type="number" min={0} max={365}
                      value={form.eligibility_min_days_since_last ?? ''}
                      onChange={e => setForm(p => ({...p, eligibility_min_days_since_last: e.target.value === '' ? null : parseInt(e.target.value) || 0}))}
                      placeholder="e.g. 14"
                      data-testid="eligibility-min"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground">Max days</Label>
                    <Input
                      type="number" min={0} max={365}
                      value={form.eligibility_max_days_since_last ?? ''}
                      onChange={e => setForm(p => ({...p, eligibility_max_days_since_last: e.target.value === '' ? null : parseInt(e.target.value) || 0}))}
                      placeholder="e.g. 21"
                      data-testid="eligibility-max"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>
                {(form.eligibility_min_days_since_last || form.eligibility_max_days_since_last) ? (
                  <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2 mt-2">
                    ⚠️ This service is for returning clients only. They must have a last visit between {form.eligibility_min_days_since_last || 0} and {form.eligibility_max_days_since_last || '∞'} days ago.
                  </p>
                ) : null}
              </div>


              {/* Length Pricing — master list in Settings */}
              <Separator className="my-3" />
              <div className="flex items-center gap-3">
                <Switch
                  checked={form.length_pricing_enabled || false}
                  onCheckedChange={v => setForm(p => ({ ...p, length_pricing_enabled: v }))}
                />
                <div>
                  <Label className="text-xs font-medium">Nail Length Pricing</Label>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    Uses the master lengths defined in <strong>Settings → Policies</strong> (Short / Medium / Long with uplifts &amp; photos)
                  </p>
                </div>
              </div>

              {/* Optional Extras */}
              <div className="mt-3">
                <Label className="text-xs font-medium mb-2 block">Optional Extras</Label>
                <div className="space-y-2">
                  {(form.extras || []).map((ext, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input value={ext.name} onChange={e => { const exts = [...(form.extras||[])]; exts[i] = {...exts[i], name: e.target.value}; setForm(p => ({...p, extras: exts})); }} className="rounded-xl text-xs h-8 flex-1" placeholder="Extra name" />
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-muted-foreground">£</span>
                        <Input type="number" step="0.5" value={ext.price} onChange={e => { const exts = [...(form.extras||[])]; exts[i] = {...exts[i], price: parseFloat(e.target.value)||0}; setForm(p => ({...p, extras: exts})); }} className="rounded-xl text-xs h-8 w-20" />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setForm(p => ({...p, extras: p.extras.filter((_,j)=>j!==i)}))} className="h-8 w-8 p-0 text-destructive"><Trash2 size={12}/></Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setForm(p => ({...p, extras: [...(p.extras||[]), {name:'', price:0}]}))} className="rounded-xl text-xs h-7"><Plus size={12} className="mr-1"/>Add extra</Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialog(null)} className="rounded-xl">Cancel</Button>
            <Button onClick={save} className="rounded-xl">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
